import { useCallback, useEffect } from 'react';
import SVGClose from 'visitor-ui-component-library-icons/icons/SVGClose';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import { hexToRgba } from 'visitor-ui-component-library/utils/hexToRgba';
import { useAriaLiveContext } from 'visitor-ui-component-library/utils/aria-live/AriaLiveContext';
import { getFocusRingStyles } from 'visitor-ui-component-library/utils/getFocusRingStyles';
import Card from 'conversations-visitor-experience-components/presentation-components/spotlight/Card';
import hashCode from 'conversations-visitor-experience-components/knowledge-base/utils/hashCode';
import styled, { keyframes } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const slideInUp = keyframes(["from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}"]);
const slideOutDown = keyframes(["from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translateY(8px);}"]);

// Purely the slide-in/out motion shell — must not carry interactive
// semantics (role/tabIndex/click/keydown), since it's an ancestor of the
// real, nested CloseButton control. See OpenTarget below.
const PopupShell = styled.div.withConfig({
  displayName: "SpotlightPopupCard__PopupShell"
})(["margin-bottom:", ";width:", "px;animation:", " 250ms ease-out forwards;@media (prefers-reduced-motion:reduce){animation-duration:1ms;}&:hover > *{border-color:", ";}"], ({
  theme
}) => theme.spotlight.spacing.sm, ({
  $width
}) => $width, ({
  $isDismissing
}) => $isDismissing ? slideOutDown : slideInUp, ({
  theme
}) => theme.spotlight.color.border);

// The clickable-to-open region. Kept as a sibling of CloseButton (not an
// ancestor) so the card's interactive role never wraps another interactive
// control — nesting role="button" around a real button is invalid and can
// cause assistive tech to flatten or hide the close control.
const OpenTarget = styled.div.withConfig({
  displayName: "SpotlightPopupCard__OpenTarget"
})(["flex:1;min-width:0;cursor:pointer;", ""], getFocusRingStyles({
  outlineOffset: '2px'
}));
const PopupCard = styled(Card).withConfig({
  displayName: "SpotlightPopupCard__PopupCard"
})(["border-radius:", ";padding:", ";border:1px solid transparent;"], ({
  theme
}) => theme.spotlight.borderRadius.lg, ({
  theme
}) => `${theme.spotlight.spacing.md} ${theme.spotlight.spacing.md} ${theme.spotlight.spacing.md} ${theme.spotlight.spacing.lg}`);
const Row = styled.div.withConfig({
  displayName: "SpotlightPopupCard__Row"
})(["display:flex;align-items:flex-start;gap:", ";"], ({
  theme
}) => theme.spotlight.spacing.sm);

// Shared base look for a popup's message copy — consumers extend this for
// their own sizing/clamping (e.g. NewMessagePreviewPopup's line-clamped
// preview text, SpotlightWelcomeMessagePopup's full-width welcome text).
export const PopupBodyText = styled.div.withConfig({
  displayName: "SpotlightPopupCard__PopupBodyText"
})(["margin:0;font-size:", ";line-height:", ";color:", ";"], ({
  theme
}) => theme.spotlight.typography.fontSize.xl, ({
  theme
}) => theme.spotlight.typography.lineHeight.lg, ({
  theme
}) => theme.spotlight.color.ink);
const CloseButton = styled(VizExIconButton).withConfig({
  displayName: "SpotlightPopupCard__CloseButton"
})(["color:", ";&:hover{background-color:", ";}"], ({
  theme
}) => theme.spotlight.color.ink, ({
  theme
}) => hexToRgba(theme.spotlight.color.ink, 0.1));
// Shared shell for the slide-in/out card popups that appear above the
// Spotlight launcher (new-message notifications, welcome message, etc.):
// owns the animation, click-to-open, keyboard, dismiss, and card chrome so
// each popup only supplies its own content.
const SpotlightPopupCard = ({
  width,
  isDismissing,
  ariaLabel,
  dismissAriaLabel,
  testId,
  onDismiss,
  onDismissAnimationEnd,
  onOpen,
  announcementText,
  badge,
  children
}) => {
  const {
    announce
  } = useAriaLiveContext();

  // role="button" (on OpenTarget below) doesn't get auto-announced like
  // role="alert" did, so announce the card's appearance explicitly when it
  // mounts — including the message text, not just the accessible name, so
  // screen-reader users get the actual message content instead of just
  // being told something arrived. Joined with a colon (matching the
  // label: value convention used elsewhere, e.g. "Attachment: {{fileName}}")
  // rather than a period, since a period reads as two separate sentences.
  //
  // AriaLiveContext's announce() dedupes by `id` alone (see announcedMessages
  // in visitor-ui-component-library/utils/aria-live/AriaLiveContext) — a
  // second call with the same id is silently dropped even if the text
  // changed. testId is static for the life of the mounted popup, so a
  // subsequent message arriving while it's still mounted must fold the
  // announced text into the id, or it never reaches the screen reader even
  // though this effect itself re-runs correctly. Hashed (rather than used
  // raw) so the id stays short and bounded regardless of message length —
  // same pattern KBArticle.tsx uses for its citation ids.
  useEffect(() => {
    announce({
      id: announcementText ? `${testId}-${hashCode(announcementText)}` : testId,
      text: announcementText ? `${ariaLabel}: ${announcementText}` : ariaLabel
    }, 'assertive');
  }, [announce, testId, ariaLabel, announcementText]);

  // No useOnEscapeKey here — the popup never receives focus when it appears
  // so the ref-scoped listener would never fire.
  const handleKeyDown = useCallback(e => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
      e.preventDefault();
      onOpen();
    }
  }, [onOpen]);
  const handleDismissClick = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  // Defensive: only react to animationend on this element itself, not a
  // bubbled event from a child's animation (e.g. UnreadCountBadge's pulse —
  // currently infinite, so it fires animationiteration rather than
  // animationend and can't trigger this today, but a future finite child
  // animation could otherwise be mistaken for this card's own exit
  // animation finishing and unmount it early).
  const handleAnimationEnd = useCallback(e => {
    if (e.target !== e.currentTarget) return;
    if (isDismissing) onDismissAnimationEnd();
  }, [isDismissing, onDismissAnimationEnd]);
  return /*#__PURE__*/_jsx(PopupShell, {
    $width: width,
    $isDismissing: isDismissing,
    onAnimationEnd: handleAnimationEnd,
    "data-test-id": `${testId}-shell`,
    children: /*#__PURE__*/_jsxs(PopupCard, {
      children: [badge, /*#__PURE__*/_jsxs(Row, {
        children: [/*#__PURE__*/_jsx(OpenTarget, {
          tabIndex: 0,
          onClick: onOpen,
          onKeyDown: handleKeyDown,
          role: "button",
          "aria-label": ariaLabel,
          "data-test-id": testId,
          children: children
        }), /*#__PURE__*/_jsx(CloseButton, {
          "aria-label": dismissAriaLabel,
          onClick: handleDismissClick,
          use: "primary-transparent-background",
          size: "sm",
          "data-test-id": `${testId}-close`,
          children: /*#__PURE__*/_jsx(VizExIcon, {
            size: "xs",
            icon: /*#__PURE__*/_jsx(SVGClose, {})
          })
        })]
      })]
    })
  });
};
SpotlightPopupCard.displayName = 'SpotlightPopupCard';
export default SpotlightPopupCard;